import api from './authService';

export interface Role {
  id: number;
  name: string;
  description?: string | null;
  group?: 'job' | 'category';
  is_system?: boolean;
  user_count?: number;
}

export interface CreateRoleData {
  name: string;
  description?: string;
  group: 'job' | 'category';
  clone_permissions_from_role_id?: number | null;
}

export interface UpdateRoleData {
  name?: string;
  description?: string | null;
  group?: 'job' | 'category';
}

export interface Department {
  id: number;
  department: string;
}

export interface ContractType {
  id: number;
  type: string;
}

export interface Office {
  id: number;
  code?: string | null;
  country: string;
  name?: string | null;
}

import { User } from '../types';

export interface CreateUserData {
  name: string;
  email: string;
  password: string;
  roles: string[];  // Now dynamic array of roles
  user_office: number;
  department: number;
  contract_type?: number;
  intern?: boolean;
  weekly_hours?: number;
  birthday?: string; // ISO date string (YYYY-MM-DD)
  trigram?: string;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  password?: string;
  roles?: string[];  // Now dynamic array of roles
  user_office?: number;
  department?: number;
  contract_type?: number;
  intern?: boolean;
  paid_leave_per_month?: number;
  weekly_hours?: number;
  birthday?: string; // ISO date string (YYYY-MM-DD)
  trigram?: string;
}

class UsersService {
  async getUsers(): Promise<User[]> {
    const response = await api.get('/auth/users');
    return response.data.users;
  }

  async createUser(userData: CreateUserData): Promise<{ message: string; user_id: number }> {
    const response = await api.post('/auth/users', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: UpdateUserData): Promise<{ message: string }> {
    const response = await api.put(`/auth/users/${userId}`, userData);
    return response.data;
  }

  async uploadProfilePicture(userId: number, file: File): Promise<{ message: string; profile_picture: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/auth/users/${userId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getRoles(): Promise<Role[]> {
    const response = await api.get('/auth/roles');
    return response.data.roles;
  }

  async createRole(roleData: CreateRoleData): Promise<{ message: string; role: Role }> {
    const response = await api.post('/auth/roles', roleData);
    return response.data;
  }

  async updateRole(roleId: number, roleData: UpdateRoleData): Promise<{ message: string; role: Role }> {
    const response = await api.put(`/auth/roles/${roleId}`, roleData);
    return response.data;
  }

  async deleteRole(roleId: number): Promise<{ message: string }> {
    const response = await api.delete(`/auth/roles/${roleId}`);
    return response.data;
  }

  async getDepartments(): Promise<Department[]> {
    const response = await api.get('/auth/departments');
    return response.data.departments;
  }

  async getContractTypes(): Promise<ContractType[]> {
    const response = await api.get('/auth/contract-types');
    return response.data.contract_types;
  }

  async getOffices(): Promise<Office[]> {
    const response = await api.get('/auth/offices');
    return response.data.offices;
  }

  async getProfile(): Promise<{ user: User }> {
    const response = await api.get('/auth/profile');
    return response.data;
  }

  async getExternUsers(): Promise<User[]> {
    const response = await api.get('/auth/users');
    // Filter to only extern users (intern is false)
    // We need to cast here as the backend might return roles as strings which TS interface restricts to specific enum
    return (response.data.users as any[]).filter((user: any) => !user.intern) as User[];
  }

  async getBirthdays(month?: number, year?: number): Promise<User[]> {
    const params: any = {};
    if (month) params.month = month;
    if (year) params.year = year;
    const response = await api.get('/auth/birthdays', { params });
    return response.data.birthdays;
  }
}

const usersService = new UsersService();
export default usersService;